import { Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { ArrowRight, Package, Layers, TrendingUp, HelpCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePartners, useProducts, usePackages, useCases, useFAQs } from '@/hooks/usePartnerships';
import { PARTNER_CONFIG } from '@/components/partnerships/PartnerTheme';
import type { PartnerSlug } from '../../../../shared/types/partnerships';
import { WinclapLoader } from '@/components/ui/winclap-loader';

function PartnerStatCard({ partnerId, slug }: { partnerId: string; slug: PartnerSlug }) {
  const { t } = useTranslation();
  const config = PARTNER_CONFIG[slug];
  const { data: products = [] } = useProducts(partnerId);
  const { data: packages = [] } = usePackages(partnerId);
  const { data: cases = [] } = useCases(partnerId);
  const { data: faqs = [] } = useFAQs(partnerId);

  const stats = [
    { label: t('partnerships.common.products'), value: products.length, icon: Layers },
    { label: t('partnerships.common.packages'), value: packages.length, icon: Package },
    { label: t('partnerships.common.cases'), value: cases.length, icon: TrendingUp },
    { label: 'FAQs', value: faqs.length, icon: HelpCircle },
  ];

  return (
    <Card className="overflow-hidden hover:shadow-md transition-shadow">
      <div className="h-1.5 w-full" style={{ backgroundColor: config.color }} />
      <div className="p-6 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold">{config.name}</h2>
          </div>
          <Link
            to={`/partnerships/${slug}`}
            className={`inline-flex items-center gap-1.5 text-sm font-medium ${config.textClass} hover:gap-2.5 transition-all`}
          >
            {t('partnerships.common.explore')} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 gap-3">
          {stats.map(({ label, value, icon: Icon }) => (
            <div key={label} className="bg-muted/40 rounded-lg p-3 space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Icon className="h-3 w-3" />
                {label}
              </div>
              <p className="text-2xl font-bold">{value}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <Link
            to={`/partnerships/${slug}/products`}
            className="flex-1 text-center text-xs py-2 px-3 rounded-md bg-muted hover:bg-muted/70 transition-colors font-medium"
          >
            {t('partnerships.common.products')}
          </Link>
          <Link
            to={`/partnerships/${slug}/packages`}
            className="flex-1 text-center text-xs py-2 px-3 rounded-md bg-muted hover:bg-muted/70 transition-colors font-medium"
          >
            {t('partnerships.common.packages')}
          </Link>
          <Link
            to={`/partnerships/${slug}/faq`}
            className="flex-1 text-center text-xs py-2 px-3 rounded-md bg-muted hover:bg-muted/70 transition-colors font-medium"
          >
            FAQ
          </Link>
        </div>
      </div>
    </Card>
  );
}

export function PartnershipsOverviewPage() {
  const { t } = useTranslation();
  const { data: partners = [], isLoading } = usePartners();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <WinclapLoader />
      </div>
    );
  }

  const partnerSlugs: PartnerSlug[] = ['tiktok', 'google', 'meta'];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">{t('partnerships.overview.title')}</h1>
        <p className="text-muted-foreground mt-1">
          {t('partnerships.overview.subtitle')}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {partnerSlugs.map(slug => {
          const partner = partners.find(p => p.slug === slug);
          if (!partner) {
            return (
              <Card key={slug} className="overflow-hidden opacity-60">
                <div className="h-1.5 w-full" style={{ backgroundColor: PARTNER_CONFIG[slug].color }} />
                <div className="p-6">
                  <h2 className="text-xl font-bold">{PARTNER_CONFIG[slug].name}</h2>
                  <p className="text-sm text-muted-foreground mt-1">{t('partnerships.common.no_data_yet')}</p>
                </div>
              </Card>
            );
          }
          return <PartnerStatCard key={slug} partnerId={partner.partnerId} slug={slug} />;
        })}
      </div>
    </div>
  );
}
